<script setup lang="ts">
  import type { AnyExtension } from '@tiptap/core';
  import type { EditorProps } from '@tiptap/pm/view';
  import type { Editor } from '@tiptap/vue-3';
  import { markRaw, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue';
  import type { Component } from 'vue';

  const props = defineProps<{
    disabled?: boolean;
    disableEditing?: boolean;
    disableInlineImages?: boolean;
    markdown?: boolean;
    singleLine?: boolean;
    editorProps?: Partial<EditorProps>;
    editorClass?: string;
    placeholderText?: string;
  }>();

  const emits = defineEmits<{
    (e: 'ready', editor: Editor): void;
    (e: 'focus'): void;
    (e: 'blur'): void;
  }>();

  const body = defineModel<string>();

  const editor = shallowRef<Editor | null>(null);
  const EditorContentComponent = shallowRef<Component | null>(null);
  const isLoading = ref(true);
  const isBlinking = ref(true);

  const stopBlinking = () => {
    isBlinking.value = false;
  };

  const handleBlur = () => {
    emits('blur');
  };

  const buildEditorClass = () =>
    [
      'prose prose-sm text-foreground focus:outline-hidden max-w-full text-sm leading-5 min-h-16 flex-1',
      props.singleLine ? 'single-line-editor' : '',
      props.disabled ? 'opacity-50 cursor-not-allowed' : '',
      props.editorClass ?? '',
    ]
      .filter(Boolean)
      .join(' ');

  // When disabled prop changes, change editability and styling
  watch(
    () => props.disabled,
    (isDisabled) => {
      editor.value?.setEditable(!isDisabled, false);
      editor.value?.setOptions({
        editorProps: {
          attributes: {
            class: buildEditorClass(),
          },
        },
      });
    },
    { immediate: true },
  );

  watch(
    editor,
    (curr, prev) => {
      if (!prev && curr) {
        emits('ready', curr);
      }
    },
    { immediate: true },
  );

  onMounted(async () => {
    // Lazy load all tiptap dependencies and custom extensions
    const [
      tiptapVue3,
      Table,
      TableRow,
      TableCell,
      TableHeader,
      Color,
      BulletList,
      HardBreak,
      ListItem,
      Paragraph,
      StarterKit,
      TextAlign,
      TextStyle,
      Underline,
      Placeholder,
      FontFamily,
      customExtensions,
      TiptapMarkdown,
    ] = await Promise.all([
      import('@tiptap/vue-3'),
      import('@tiptap/extension-table'),
      import('@tiptap/extension-table-row'),
      import('@tiptap/extension-table-cell'),
      import('@tiptap/extension-table-header'),
      import('@tiptap/extension-color'),
      import('@tiptap/extension-bullet-list'),
      import('@tiptap/extension-hard-break'),
      import('@tiptap/extension-list-item'),
      import('@tiptap/extension-paragraph'),
      import('@tiptap/starter-kit'),
      import('@tiptap/extension-text-align'),
      import('@tiptap/extension-text-style'),
      import('@tiptap/extension-underline'),
      import('@tiptap/extension-placeholder'),
      import('@tiptap/extension-font-family'),
      import('./extensions'),
      props.markdown ? import('tiptap-markdown') : null,
    ]);

    // Store EditorContent component for template
    EditorContentComponent.value = markRaw(tiptapVue3.EditorContent);

    // Configure extensions
    BulletList.default.configure({
      keepAttributes: true,
      keepMarks: false,
    });

    Color.default.configure({
      types: ['textStyle'],
    });

    FontFamily.default.configure({
      types: ['textStyle'],
    });

    customExtensions.FontSize.configure({
      types: ['textStyle'],
    });

    ListItem.default.configure({
      HTMLAttributes: {
        class: '[&>p]:inline-block',
      },
    });

    TextAlign.default.configure({
      alignments: ['left', 'center', 'right'],
      defaultAlignment: 'left',
      types: ['heading', 'paragraph', 'div'],
    });

    // Build extensions array
    const extensions: AnyExtension[] = [
      Color.default,
      StarterKit.default,
      customExtensions.ControlClickLink.configure({
        openOnClick: false,
        validate: (href: string) => /^https?:\/\//.test(href),
      }),
      Placeholder.default.configure({
        placeholder: props.placeholderText || '',
      }),
      TextAlign.default,
      TextStyle.default,
      Underline.default,
      FontFamily.default,
      customExtensions.FontSize,
      customExtensions.JinjaVariable,
      HardBreak.default,
      Table.default
        .extend({
          addAttributes() {
            return {
              ...this.parent?.(),
              style: {
                default: null,
                parseHTML: (element: HTMLElement) => element.getAttribute('style'),
                renderHTML: (attributes: Record<string, unknown>) => {
                  if (!attributes.style) {
                    return {};
                  }
                  return { style: attributes.style };
                },
              },
            };
          },
        })
        .configure({ resizable: true, handleWidth: 5, cellMinWidth: 60 }),
      TableRow.default.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: (element: HTMLElement) => element.getAttribute('style'),
              renderHTML: (attributes: Record<string, unknown>) => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      TableHeader.default.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: (element: HTMLElement) => element.getAttribute('style'),
              renderHTML: (attributes: Record<string, unknown>) => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      TableCell.default.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: (element: HTMLElement) => element.getAttribute('style'),
              renderHTML: (attributes: Record<string, unknown>) => {
                if (!attributes.style) {
                  return {};
                }
                return { style: attributes.style };
              },
            },
          };
        },
      }),
      Paragraph.default.extend({
        parseHTML() {
          return [
            { tag: 'p' },
            {
              getAttrs: () => {
                return { preserveEmpty: true };
              },
              tag: 'div',
            },
          ];
        },
        renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
          return [
            'div',
            tiptapVue3.mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
            0,
          ];
        },
      }),
    ];

    if (!props.disableInlineImages) {
      extensions.push(customExtensions.Image.configure({ allowBase64: true }));
    }

    if (TiptapMarkdown) {
      extensions.push(
        TiptapMarkdown.Markdown.configure({
          html: true,
          breaks: true,
          linkify: true,
          transformPastedText: true,
          transformCopiedText: true,
        }),
      );
    }

    // Create editor instance
    editor.value = markRaw(
      new tiptapVue3.Editor({
        content: body.value,
        editable: !props.disableEditing,
        editorProps: {
          attributes: {
            class: buildEditorClass(),
          },
          ...props.editorProps,
        },
        extensions,
        injectCSS: true,
        onFocus() {
          emits('focus');
        },
        onUpdate: () => {
          if (editor.value) {
            body.value = props.markdown
              ? (editor.value.storage.markdown?.getMarkdown() ?? editor.value.getHTML())
              : editor.value.getHTML();
          }
        },
      }),
    );

    editor.value.on('focus', stopBlinking);
    editor.value.on('blur', handleBlur);

    isLoading.value = false;
  });

  onUnmounted(() => {
    if (editor.value) {
      editor.value.off('focus', stopBlinking);
      editor.value.off('blur', handleBlur);
      editor.value.destroy();
    }
  });
</script>

<template>
  <div v-if="isLoading" class="flex flex-1 min-h-16 items-center justify-center">
    <i class="fa fa-spinner fa-spin text-muted-foreground" />
  </div>
  <component
    :is="EditorContentComponent"
    v-else-if="EditorContentComponent"
    :editor="editor"
    class="flex flex-1"
    :class="{ 'blinking-cursor': isBlinking }"
    @focus="emits('focus')"
  />
</template>

<style lang="postcss">
  @reference "../../globals.css";

  .prose {
    a {
      @apply text-blue;
    }

    :where(p) {
      margin-top: 0;
      margin-bottom: 0;
    }

    :where(li)::marker {
      @apply text-muted-foreground;
    }

    .tableWrapper {
      margin: 1em 0;
      overflow-x: auto;
    }

    /* A table nested inside another table's cell — e.g. the custom-table
       line-items sub-table — sits directly beneath its parent row. Drop the
       standalone wrapper margin (above) and the prose typography table margin
       (margin-top/bottom: 2em) so it doesn't leave a gap under the parent row.
       Scoped to nested tables only (inside a td); top-level tables are
       untouched. `!important` overrides the typography plugin's cascade layer. */
    td .tableWrapper,
    td table {
      margin: 0 !important;
    }

    table {
      border-collapse: collapse;
      table-layout: fixed;
      width: 100%;
      overflow: hidden;
    }

    table td,
    table th {
      border: 1px solid var(--border);
      padding: 0.5rem 0.75rem;
      vertical-align: top;
      position: relative;
      min-width: 4rem;
      box-sizing: border-box;
    }

    table th {
      font-weight: 600;
      background-color: var(--muted);
      text-align: left;
    }

    table p {
      margin: 0;
    }
  }

  /* Pull heading sizes down to the app-wide scale set in main.css.
     prose-sm's defaults (h1≈30px, h2≈20px) read larger than the rest of the
     app, causing a visible jump between editor and non-prose readonly views. */
  .prose.prose-sm :where(h1) {
    @apply text-xl font-bold;
  }
  .prose.prose-sm :where(h2) {
    @apply text-lg font-semibold;
  }
  .prose.prose-sm :where(h3) {
    @apply text-base font-semibold;
  }
  .prose.prose-sm :where(h4) {
    @apply text-sm font-medium;
  }

  .ProseMirror table .selectedCell::after {
    background: color-mix(in oklch, var(--primary) 18%, transparent);
    content: '';
    inset: 0;
    pointer-events: none;
    position: absolute;
    z-index: 2;
  }

  .ProseMirror table .column-resize-handle {
    background-color: var(--primary);
    bottom: -2px;
    pointer-events: none;
    position: absolute;
    right: -2px;
    top: 0;
    width: 4px;
  }

  .ProseMirror.resize-cursor {
    cursor: ew-resize;
    cursor: col-resize;
  }

  /* Use `is-editor-empty` (set only when the whole document is empty) rather
     than `is-empty` (set on any empty node) so the placeholder shows only when
     there's no text at all — not on every blank line the user adds with Return. */
  .ProseMirror .is-editor-empty::before {
    content: attr(data-placeholder);
    color: #adb5bd;
    float: left;
    height: 0;
    opacity: 0.8;
  }

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }

  .single-line-editor {
    @apply whitespace-nowrap overflow-x-auto overflow-y-hidden;
    display: block;
    cursor: text;
  }

  .single-line-editor::-webkit-scrollbar {
    display: none;
  }

  .single-line-editor * {
    display: inline !important;
    white-space: nowrap !important;
  }

  /* Resizable image styles */
  .ProseMirror .image-wrapper {
    display: inline-block;
    position: relative;
    max-width: 100%;
    line-height: 0;
    font-size: 0;
    overflow: visible;
  }

  .ProseMirror .image-wrapper img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 0;
    padding: 0;
  }

  .ProseMirror .image-wrapper.ProseMirror-selectednode {
    outline: 2px solid #ff6a13;
  }

  .ProseMirror .image-wrapper .resize-handle {
    position: absolute;
    right: -5px;
    bottom: -5px;
    width: 12px;
    height: 12px;
    background: #ff6a13;
    border: 2px solid white;
    border-radius: 50%;
    cursor: nwse-resize;
    opacity: 0;
    transition: opacity 0.2s;
    pointer-events: auto;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }

  .ProseMirror .image-wrapper:hover .resize-handle {
    opacity: 1;
  }

  .ProseMirror .image-wrapper img {
    pointer-events: none;
  }
</style>
